import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits, useRealtimeTags } from 'Core/hooks';
import { useIntl } from "react-intl";
// Components
import { Grid, Typography, Box, Divider } from "@mui/material";
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { Button, Form, Input, Select, InputAvatar } from "Components";

import { NotificationManager } from "Components/Notifications";
import TagsManage from 'Modules/.common/components/TagsManage';
import { HasPermission } from "Core/security";
import "./styles.scss";
// Actions
import {
  searchCellCarriers,
  openDriverCreateEditModal,
  catalogLoad,
  createDriverAvatar,
  clearRedux,
  searchEntityLists,
  searchSupervisor,
  getSecureFile
} from "Redux/actions";
import { reset, updateDriver, createDriver } from "./redux/slices";
import CreateEditTag from "Modules/account/views/tags/components/createEditTag";
import { useSupervisorPermission } from "../../../../../.common/utils/supervisorPermission";
import { Checkbox, DatePicker, Switch, TextArea } from "@gpstrackit/gtc-react-controls";
import { GetFormatForMoment, getCountryCodes } from 'Core/data/Helpers';

import {
  getCountryCodeById,
  getEntityById,
  splitPhoneNumber,
  validateLicenseState
} from "./utils";
import { validEmail } from "../../../../../../core/hooks/utils";

import PasswordDriverFields from "../passwordDriverFields";

const convertKmToMiles = (km) => {
  return km ? Number((km * 0.621371192).toFixed(5)) : null;
};

const convertMilesToKm = (miles) => {
  return miles ? Number((miles * 1.609344).toFixed(5)) : null;
};

const today = new Date();
const endOfYearPlus10 = new Date(new Date().getFullYear() + 10, 11, 31, 23, 59, 59, 999);

const CounterString = ({ string, maxLength }) => {
  return (
    <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
      <Typography style={{ fontSize: 12, color: "#666" }}>{string.length} / {maxLength}</Typography>
    </Box>
  );
}

const ItemWrapper = ({ props, children }) => {
  return (
    <Box px="16px" mt="0px" {...props}>
      {children}
    </Box>
  );
};

const ItemLabel = ({ isRequired = false, children, marginBottom }) => {
  return (
    <p style={{ fontWeight: "bold", margin: 0, marginBottom: marginBottom || "-10px" }}>
      {isRequired && <span style={{ color: "red", marginRight: "5px" }}>*</span>}
      {children}
    </p>
  );
};

const MIN_PC_MILES = 5;
const MIN_PC_KM = 8.04672;

const getDefaultPcDistance = (isKph) => (isKph ? MIN_PC_KM : MIN_PC_MILES);

const buildFullPhone = (indicator, phone) => {
  if (indicator?.id && phone) {
    return `+${indicator.id}${phone}`;
  }

  return null;
};



const CreateEditDriver = (props) => {
  // Intl
  const { messages } = useIntl();

  // Props
  const { onDriverSaved } = props;

  // Hooks
  const dispatch = useDispatch();

  //State local
  const [formData, setFormData] = useState({});
  const [showTagsError, setShowTagsError] = useState(false);
  const [errorMatchPassword, setErrorMatchPassword] = useState();
  const [isRequiredPwd, setIsRequiredPwd] = useState();
  const countries = useMemo(() => getCountryCodes(), []);

  //Redux
  const vehicles = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const timeZones = useSelector((state) => state.catalogsRedux.timeZones);
  const loadingTimeZones = useSelector(
    (state) => state.catalogsRedux.loadingTimeZones
  );

  const {
    loading: loadingCarriers,
    cellCarriers,
    formOptionsTypeSelected: contactOptions,
  } = useSelector((state) => state.contactsRedux);

  const { supervisor, loadingSupervisor, errorSupervisor } = useSelector(
    (state) => state.driverTableSliceRedux
  );

  const user = useSelector((state) => state.securityRedux.user);
  const { isKph } = user;
  const measure = isKph ? 'km' : 'mi';




  const loading = useSelector((state) => state.driverCreateEditSliceRedux.loading);
  const openCreateEdit = useSelector((state) => state.driverCreateEditSliceRedux.openCreateEdit);
  const driverResponse = useSelector((state) => state.driverCreateEditSliceRedux.driverResponse);
  const driverAvatar = useSelector((state) => state.driverCreateEditSliceRedux.driverAvatar);
  const loadingEntities = useSelector((state) => state.driverCreateEditSliceRedux.loadingEntities);
  const errorEntities = useSelector((state) => state.driverCreateEditSliceRedux.errorEntities);
  const entityCountryList = useSelector((state) => state.driverCreateEditSliceRedux.entityCountryList);
  const entityStateList = useSelector((state) => state.driverCreateEditSliceRedux.entityStateList);
  const entityHosRuleSetList = useSelector((state) => state.driverCreateEditSliceRedux.entityHosRuleSetList);
  const error = useSelector((state) => state.driverCreateEditSliceRedux.error);
  const driverById = useSelector((state) => state.driverCreateEditSliceRedux.driverById);
  const loadingDriverById = useSelector((state) => state.driverCreateEditSliceRedux.loadingDriverById);
  const secureUrl = useSelector((state) => state.commomRedux?.updateDriver);
  const userSecurity = useSelector((state) => state.securityRedux.user);

  const { loadingCurrentSubUser, currentSubUser } = useSelector(
    (state) => state.subUserRedux
  );

  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const loadingTags = useSelector((state) => state.catalogsRedux.loadingTags);
  const hasZonarELDPermissions =
    HasPermission(user, "evir") ||
    HasPermission(user, "zlogs");

  const hasKeyFobPermissions = HasPermission(user, "ckeyfob", [42]);
  const hasELDPermission = HasPermission(user, "zlogs");

  const statusOptionDriver = [
    { value: 1, name: messages["active"] },
    { value: 0, name: messages["inactive"] },
  ];

  //Ref
  const formRef = useRef();

  const buildDriverTableRow = useCallback((driverResult) => {
    const fullPhone = buildFullPhone(formData?.indicator, formData?.phone);
    const supervisor = formData?.supervisor
      ? {
          firstName: formData.supervisor.firstName || "",
          lastName: formData.supervisor.lastName || "",
        }
      : null;

    return {
      ...driverResult,
      id: String(driverResult?.driverId || formData?.id),
      driverId: driverResult?.driverId || formData?.id,
      name: driverResult?.name || formData?.name,
      externalId: driverResult?.externalId ?? formData?.externalId ?? "",
      tagIds: formData?.tags?.map((tag) => tag?.id) || [],
      tags: driverResult?.tags || formData?.tags || [],
      status: driverResult?.status ?? formData?.status?.value ?? 1,
      idTimeZone: driverResult?.timeZone?.id || driverResult?.idTimeZone || formData?.timezone?.id || null,
      timeZone: driverResult?.timeZone || formData?.timezone || null,
      licenseNumber: driverResult?.licenseNumber ?? formData?.licenseNumber ?? null,
      licenseCountry: driverResult?.licenseCountry ?? formData?.licenseCountry?.entityId ?? null,
      licenseState: driverResult?.licenseState ?? formData?.licenseState?.entityId ?? null,
      driverPin: driverResult?.driverPin ?? formData?.driverPin ?? null,
      hosRulesetIds: driverResult?.hosRulesetIds ?? (formData?.hosRulesetIds ? JSON.stringify([Number(formData?.hosRulesetIds?.entityId)]) : null),
      hosExempt: driverResult?.hosExempt ?? formData?.hosExempt ?? false,
      hosExemptComment: driverResult?.hosExemptComment ?? formData?.hosExemptComment ?? null,
      email: driverResult?.email ?? (formData?.email ? formData.email.trim() : null),
      phone: driverResult?.phone ?? fullPhone,
      notifyByEmail: driverResult?.notifyByEmail ?? formData?.notifyByEmail ?? false,
      notifyBySms: driverResult?.notifyBySms ?? formData?.notifyBySms ?? false,
      supervisorId: driverResult?.supervisorId ?? formData?.supervisor?.id ?? null,
      supervisor: driverResult?.supervisor ?? supervisor,
      fsmDriver: driverResult?.fsmDriver ?? (formData?.username ? { username: formData.username } : null),
      fsmUsername: driverResult?.fsmDriver?.username || formData?.username || "",
      keyfob: driverResult?.keyfob ?? (formData?.keyFobHex ? { hex: formData.keyFobHex } : null),
      avatarFileId: driverResult?.avatarFileId ?? formData?.avatarFileId ?? null,
      avatarFile: driverResult?.avatarFile ?? null,
      tempAvatarUrl: driverResult?.tempAvatarUrl ?? null,
      specialDutyStatuses: driverResult?.specialDutyStatuses ?? null,
      empNum: driverResult?.empNum ?? formData?.empNum ?? null,
      unitId: driverResult?.unit?.id ?? formData?.unit?.id ?? null,
      unit: driverResult?.unit || (formData?.unit ? {
        id: formData.unit.id,
        label: formData.unit.label,
      } : null),
    };
  }, [formData]);

  const vaidateUploadAvatar = useCallback(() => {
    if (formData?.inputAvatar) {
      dispatch(
        createDriverAvatar({
          ...formData.inputAvatar,
          fileKeyDelete: formData?.avatarFileId || driverById?.avatarFileId || null,
        })
      );
      return;
    }
    changeData();
  });

  const changeData = useCallback((uploadedAvatarFileId = undefined) => {
    if (!formData?.tags?.length) {
      setShowTagsError(true);
      return;
    }

    if (formData?.username && !formData?.password && !formData?.id) {
      NotificationManager.warning(
        messages["settingsDriver_passwordRequired"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
      return;
    }

    if (formData?.password !== formData?.repeatPassword) {
      setErrorMatchPassword(
        messages["settingsDriver_repeatPasswordDoNotMatch"]
      );
      return;
    }

    if (formData?.email && !validEmail(formData?.email)) {
      NotificationManager.warning(
        messages["settingsDriver_invalidEmail"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
      return;
    }

    if (formData?.driverPin && formData?.driverPin.length < 4) {
      NotificationManager.error(
        messages["settingsDriver_exactLengthMessagePin"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
      return;
    }

    if (
      hasZonarELDPermissions &&
      formData?.hosExempt &&
      !formData?.hosExemptComment
    ) {
      NotificationManager.error(
        messages["settingsDriver_zonarELDExemptCommentError"],
        messages["error"],
        3000,
        null,
        null,
        "warning"
      );
      return;
    }

    const phone = buildFullPhone(formData?.indicator, formData?.phone);

    let externalIdForApi = formData.externalId;
    if (hasZonarELDPermissions) {
      const externalTrim = String(formData?.externalId ?? "").trim();
      if (externalTrim.length < 3) {
        return;
      }
      externalIdForApi = externalTrim;
    }

    const { licenseCountry, licenseState } = validateLicenseState(formData);
    const licenseNumberTrimmed = String(formData?.licenseNumber ?? "").trim();

    if (hasELDPermission) {
      if (!licenseNumberTrimmed || !licenseCountry || !licenseState) {
        NotificationManager.warning(
          messages["required_field"],
          messages["warning"],
          3000,
          null,
          null,
          "warning"
        );
        return;
      }
    }

    // Convertir allowedPCDistance a millas si está en kilómetros
    let allowedPCDistanceInMiles = formData?.allowedPCDistance;
    if (isKph && formData?.allowedPCDistance) {
      allowedPCDistanceInMiles = convertKmToMiles(formData?.allowedPCDistance);
    }

    if (formData?.personalConveyance && allowedPCDistanceInMiles < 5) {
      NotificationManager.error(
        `${messages["settingsDriver_allowedPCDistanceError"]} ${measure === 'km' ? '8.04672' : '5'} ${measure}`,
        messages["error"],
        3000,
        null,
        null,
        "warning"
      );
      return;
    }

    const specialDutyStatuses = {
      PC: formData?.personalConveyance || false,
      YM: formData?.personalYardMoves || false,
      PCD: allowedPCDistanceInMiles?.toString() || null,
    }

    const finalAvatarFileId =
      uploadedAvatarFileId !== undefined
        ? uploadedAvatarFileId
        : (formData?.avatarFileId ?? null);

    
    let data = {
      name: formData.name,
      externalId: externalIdForApi,
      groupId: formData?.driverById?.id || null,
      tagIds: formData?.tags?.map((tag) => tag?.id),
      timeZoneId: formData?.timezone?.id || null,
      unitId: formData?.unit?.id|| null,
      username: formData?.username || null,
      password: formData?.password || null,
      avatarFileId: finalAvatarFileId,
      status: formData?.status
        ? formData?.status.value
        : statusOptionDriver[0].value,
      supervisorId: formData?.supervisor?.id,
      supervisor: formData?.supervisor || null,
      licenseNumber: hasELDPermission
        ? licenseNumberTrimmed || null
        : formData?.licenseNumber || null,
      licenseCountry: licenseCountry || null,
      licenseState: licenseState || null,
      driverPin: formData?.driverPin || null,
      hosRulesetIds:
        formData?.hosRulesetIds ? JSON.stringify([Number(formData?.hosRulesetIds?.entityId)]) : null,
      hosExempt: formData?.hosExempt || false,
      email: formData?.email ? formData?.email?.trim() : null,
      phone: phone,
      hosExemptComment: formData?.hosExempt
        ? formData?.hosExemptComment || null
        : null,
      notifyByEmail: formData?.notifyByEmail || false,
      notifyBySms: formData?.notifyBySms || false,
      keyFobHex:
        driverById?.keyfob?.hex === formData?.keyFobHex
          ? null
          : formData?.keyFobHex || null,
      specialDutyStatuses: JSON.stringify(specialDutyStatuses),
      empNum: formData?.empNum || null,
      licenseExpDate: formData?.licenseExpDate
        ? new Date(formData.licenseExpDate).toISOString().slice(0, 10)
        : null,
      hasVideoAccess: !!formData?.hasVideoAccess,
    };
    formData.id
      ? dispatch(updateDriver({ id: formData.id, ...data }))
      : dispatch(createDriver(data));
  });

  const handleClose = useCallback(() => {
    setFormData({});
    dispatch(openDriverCreateEditModal({ open: false, driverGroup: null }));
    dispatch(reset());
    setErrorMatchPassword();
  });

  useEffect(() => {
    if (driverAvatar?.fileKey) {
      changeData(driverAvatar?.id);
    }
  }, [driverAvatar]);

  useEffect(() => {
    if (driverById && cellCarriers.length) {
      let data = {};
      let avatar = {};
      let driverTags = [];
      let tagIds = [];
      if (driverById?.tagIds) {
        try {
          tagIds = JSON.parse(driverById?.tagIds);
        } catch (e) {
          tagIds = [];
        }

        if (tagIds[0] === -99) {
          driverTags = [{ id: -99 }];
        } else {
          driverTags = tags?.filter((tag) => {
            return tagIds?.find((id) => id === tag?.id);
          });
        }
      }

      let indicator = null;
      const { countryCode, phone } = splitPhoneNumber(driverById?.phone);
      if (countryCode) {
        indicator = getCountryCodeById(countryCode);
      }

      const findCountry = getEntityById(
        driverById?.licenseCountry,
        entityCountryList
      );

      const findState = getEntityById(
        driverById?.licenseState,
        findCountry?.entityId === "US" || findCountry?.entityId === "CA" ? entityStateList : entityCountryList
      );

      let hosRulesetIds = null;
      try {
        hosRulesetIds = JSON.parse(driverById?.hosRulesetIds);
      } catch (e) {
        hosRulesetIds = null;
      }

      const findHosRuleset = getEntityById(
        hosRulesetIds?.length > 0 ? hosRulesetIds[0] : null,
        entityHosRuleSetList
      );

      const specialDutyStatuses = driverById?.specialDutyStatuses ? JSON.parse(driverById?.specialDutyStatuses) : null;

      // Convert allowedPCDistance from miles to kilometers if the user is in KPH mode; preserve up to 5 decimals
      let allowedPCDistanceDisplay = specialDutyStatuses?.PCD ? Number(specialDutyStatuses?.PCD) : null;
      if (isKph && allowedPCDistanceDisplay) {
        allowedPCDistanceDisplay = convertMilesToKm(allowedPCDistanceDisplay);
      }
      if (allowedPCDistanceDisplay != null && Number.isFinite(allowedPCDistanceDisplay)) {
        allowedPCDistanceDisplay = Math.round(allowedPCDistanceDisplay * 100000) / 100000;
      }

      data = {
        id: driverById.id,
        name: driverById.name,
        externalId: driverById.externalId,
        unit: vehicles.find((u) => u.id == driverById?.unit?.id),
        timezone: timeZones.find((tz) => tz.id == driverById.idTimeZone),
        username: driverById?.fsmDriver?.username,
        avatarFileId: driverById?.avatarFile?.id || null,
        avatar: avatar,
        tags: driverTags,
        status: statusOptionDriver.find(
          (res) => res.value === driverById.status
        ),
        supervisor: supervisor?.find(
          (res) => res.id === driverById?.supervisorId
        ),
        licenseNumber: driverById?.licenseNumber,
        licenseState: findState,
        licenseCountry: findCountry,
        driverPin: `${driverById?.driverPin}`.toString(),
        hosRulesetIds: findHosRuleset,
        hosExempt: driverById?.hosExempt || false,
        email: driverById?.email,
        hosExemptComment: driverById?.hosExemptComment || null,
        phone: phone,
        indicator,
        notifyByEmail: driverById?.notifyByEmail || false,
        notifyBySms: driverById?.notifyBySms || false,
        keyFobHex: driverById?.keyfob?.hex || null,
        personalConveyance: specialDutyStatuses?.PC || null,
        personalYardMoves: specialDutyStatuses?.YM || null,
        allowedPCDistance: allowedPCDistanceDisplay,
        empNum: driverById?.empNum || "",
        licenseExpDate: driverById?.licenseExpDate
          ? new Date(driverById.licenseExpDate)
          : null,
        hasVideoAccess: driverById?.fsmDriver?.hasVideoAccess || false,
      };

      if (driverById.contactInfo) {
        let contact = driverById.contactInfo.split("@");
        let isNumber = !isNaN(contact[0]);
        let type = isNumber ? "cellphone" : "email";

        if (isNumber) {
          data = {
            ...data,
            phone: contact[0],
            carrier: cellCarriers.filter(
              (item) => item.gateway === contact[1]
            )[0],
          };
        } else {
          data = {
            ...data,
            address: driverById.contactInfo,
          };
        }
      }
      if (driverById.groupId) {
        data = {
          ...data,
          driverById: {
            id: driverById.groupId,
            name: driverById.groupName,
          },
        };
      }

      setIsRequiredPwd(data?.username);
      setFormData(data);

      if (driverById?.avatarFile?.fileKey) {
        dispatch(getSecureFile({ fileKey: driverById?.avatarFile?.fileKey, timeout: 600, fixedKey: 'updateDriver' }))
      }
    }
  }, [
    driverById,
    cellCarriers,
    supervisor,
    entityHosRuleSetList,
    entityCountryList,
    entityStateList,
  ]);

  useEffect(() => {
    if (driverResponse && openCreateEdit) {
      if (driverResponse?.error) {
        NotificationManager.error(
          messages[driverResponse?.error] || messages["errorMessage"],
          messages["error"],
          3000,
          null,
          null,
          "warning"
        );
      } else {
        onDriverSaved && onDriverSaved(buildDriverTableRow(driverResponse));

        let messageResponse = driverById
          ? messages["settingsDriver_updated"]
          : messages["settingsDriver_created"];
        NotificationManager.success(
          messageResponse,
          messages["success"],
          3000,
          null,
          null,
          "Success"
        );

        handleClose();
      }
    }
    dispatch(catalogLoad(["drivers", "timeZones"], true));
  }, [driverResponse, openCreateEdit, buildDriverTableRow, dispatch, driverById, onDriverSaved, messages]);

  useEffect(() => {
    dispatch(searchCellCarriers());
    dispatch(searchSupervisor({}));
  }, []);

  useEffect(() => {
    if (error) {
      let errorMessage = messages["settingsDriver_errorMessageCreateDriver"];
      if (error === "Code duplicated") {
        errorMessage = messages["settingsDriver_codeDuplicated"];
      } else if (typeof error === "string") {
        errorMessage = messages[error] || error;
      }
      NotificationManager.error(
        errorMessage,
        messages["error"],
        3000,
        null,
        null,
        "warning"
      );
      dispatch(reset({ error: null }));
    }
  }, [error]);

  useEffect(() => {
    dispatch(searchEntityLists({ terms: ["COUNTRY", "STATE", "hosRuleSet"], entityName: "entityName" }));
    
    // Inicializar formData cuando se abre el modal para crear (no editar)
    if (openCreateEdit && !driverById) {
      setFormData(prevFormData => ({ ...prevFormData, hasVideoAccess: false }));
    }
    
    return () => {
      dispatch(clearRedux("CONTACTS"));
      dispatch(clearRedux("COMMON", { updateDriver: null }));
    };
  }, [openCreateEdit]);

  useEffect(() => {
    if (secureUrl) {
      setFormData({
        ...formData,
        avatar: { url: secureUrl }
      });
    }
  }, [secureUrl])

  const sortByLabel = (data) => {
    return data.slice().sort((a, b) => {
      const labelA = a.label.toUpperCase();
      const labelB = b.label.toUpperCase();

      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    });
  }

  return (
    <>
      <Modal
        title={
          loadingDriverById ? messages[`loading`] :
            !driverById
              ? messages[`settingsDriver_create`]
              : `${messages[`settingsDriver_edit`]} ${formData?.name}`
        }
        open={openCreateEdit}
        loading={
          loading ||
          loadingCarriers ||
          loadingTimeZones ||
          loadingTags ||
          loadingSupervisor ||
          loadingCurrentSubUser
        }
        size="small"
        handleClose={handleClose}
        position="right"
        style={{ overflow: "hidden" }}
        className="overflow-hidden"
      >
        <ModalBody
          style={{
            overflow: "hidden",
            marginBottom: 0,
            fontSize: 12,
          }}
        >
          <div
            style={{
              padding: "0px 24px 24px 24px",
              height: "calc(100vh - 200px)",
              overflow: "auto",
            }}
          >
            <Form ref={formRef} onValidSubmit={vaidateUploadAvatar}>
              <Grid
                container
                justifyContent="center"
                direction="column"
                className="paddingBody"
                spacing={2}
              >
                <ItemWrapper>
                  <Box style={{ marginTop: 16 }}>
                    <InputAvatar
                      deleteAvatar={() => {
                        setFormData({ ...formData, avatar: {}, avatarFileId: null, inputAvatar: undefined });
                      }}
                      value={formData?.avatar}
                      name={formData?.name}
                      label={messages["settingsDriver_avatar"]}
                      required={false}
                      onChange={(value) => {
                        setFormData({ ...formData, inputAvatar: value });
                      }}
                    />
                  </Box>
                </ItemWrapper>

                <ItemWrapper>
                  <ItemLabel isRequired={hasZonarELDPermissions}>{messages["settingsDriver_externalId"]}</ItemLabel>
                  <Input
                    id="externalId"
                    type="text"
                    regex={/^[A-Za-z0-9_-]*$/}
                    placeholder={messages["settingsDriver_externalId"]}
                    value={formData?.externalId || ""}
                    onChange={(value) => {
                      setFormData({ ...formData, externalId: value });
                    }}
                    maxLength={40}
                    maxLengthMessage={`${messages["settingsDriver_maxLengthMessageName"]} 40`}
                    minLength={hasZonarELDPermissions ? 3 : 0}
                    minLengthMessage={messages["settingsDriver_minLengthMessageExternalId"]}
                    required={hasZonarELDPermissions}
                    requiredMessage={messages["required_field"]}
                  />
                  <CounterString string={formData?.externalId || ""} maxLength={40} />
                </ItemWrapper>

                <ItemWrapper>
                  <ItemLabel isRequired={true}>{messages["settingsDriver_driver"]}</ItemLabel>
                  <Input
                    id="name"
                    type="text"
                    placeholder={messages["settingsDriver_driver"]}
                    value={formData?.name || ""}
                    onChange={(value) => {
                      setFormData({ ...formData, name: value });
                    }}
                    maxLength={100}
                    maxLengthMessage={`${messages["settingsDriver_maxLengthMessageName"]} 100`}
                    required
                    requiredMessage={messages["required_field"]}
                  />
                  <CounterString string={formData?.name || ""} maxLength={100} />
                </ItemWrapper>
                {userSecurity?.zonarIntegration==1 && (
                  <ItemWrapper>
                    <ItemLabel isRequired={(hasZonarELDPermissions && !formData?.keyFobHex) ? true : false}>{messages["settingsDriver_employeeNumber"]}</ItemLabel>
                    <Input
                      id="empNum"
                      type="text"
                      placeholder={messages["settingsDriver_employeeNumber"]}
                      value={formData?.empNum || ""}
                      onChange={(value) => {
                        const cleaned = String(value).replace(/[^A-Za-z0-9]/g, "").slice(0, 20);
                        setFormData({ ...formData, empNum: cleaned });
                      }}
                      regex={/^[A-Za-z0-9]*$/}
                      maxLength={20}
                      maxLengthMessage={`${messages["settingsDriver_maxLengthMessageName"]} 20`}
                      minLength={
                        hasZonarELDPermissions && !formData?.keyFobHex ? 12 : 0
                      }
                      minLengthMessage={messages["settingsDriver_minLengthMessageEmployeeNumber"]}
                      required={(hasZonarELDPermissions && !formData?.keyFobHex) ? true : false}
                      requiredMessage={(hasZonarELDPermissions && !formData?.keyFobHex) ? messages["required_field"] : null}
                    />
                    <CounterString string={formData?.empNum || ""} maxLength={20} />
                  </ItemWrapper>
                )}
                {hasZonarELDPermissions && (
                  <ItemWrapper>
                    <ItemLabel isRequired={hasELDPermission ? true : false}>{messages["settingsDriver_driverPin"]}</ItemLabel>
                    <Input
                      id="driverPin"
                      style={{ width: "95%" }}
                      type="number"
                      onlyPositive
                      preventOnChange
                      placeholder={messages["settingsDriver_driverPin"]}
                      value={formData?.driverPin || ""}
                      required={hasELDPermission ? true : false}
                      requiredMessage={messages["required_field"]}
                      onChange={(value) => {
                        const parseString = String(value);
                        if (
                          parseString === null ||
                          parseString === "" ||
                          parseString === "-"
                        ) {
                          setFormData({ ...formData, driverPin: null });
                        } else {
                          const regex = /^[0-9]+$/;
                          if (regex.test(parseString) && parseInt(parseString) <= 9999) {
                            setFormData({ ...formData, driverPin: value });
                          }
                        }
                      }}
                      maxLength={4}
                      minLength={4}
                      minLengthMessage={`${messages["settingsDriver_exactLengthMessagePin"]}`}
                      maxLengthMessage={`${messages["settingsDriver_exactLengthMessagePin"]}`}
                    />
                    <CounterString string={formData?.driverPin || ""} maxLength={4} />
                  </ItemWrapper>
                )}

                <ItemWrapper>
                  <ItemLabel isRequired={hasELDPermission ? true : false}>{messages["settingsDriver_licenseNumber"]}</ItemLabel>
                  <Input
                    id="licenseNumber"
                    type="text"
                    placeholder={messages["settingsDriver_licenseNumber"]}
                    required={hasELDPermission ? true : false}
                    value={formData?.licenseNumber || ""}
                    requiredMessage={messages["required_field"]}
                    onChange={(value) => {
                      setFormData({ ...formData, licenseNumber: value });
                    }}
                    maxLength={15}
                    minLength={hasELDPermission ? 7 : 0}
                    maxLengthMessage={`${messages["settingsDriver_maxLengthMessageName"]} 15`}
                    minLengthMessage={`${messages["settingsDriver_minLengthMessageLicenseNumber"]}`}
                  />
                  <CounterString string={formData?.licenseNumber || ""} maxLength={15} />
                </ItemWrapper>

                <ItemWrapper>
                  <ItemLabel isRequired={false} marginBottom={'0'}>{messages["settingsDriver_licenseExpDate"]}</ItemLabel>
                  <DatePicker
                    id="licenseExpDate"
                    isClearable
                    showButtons
                    showTimePicker={false}
                    style={{ marginBottom: 8 }}
                    calendarPosition="left"
                    placeholder={messages["settingsDriver_licenseExpDate"]}
                    format={GetFormatForMoment(user?.dateformat)}
                    minDate={today}
                    maxDate={endOfYearPlus10}
                    value={formData?.licenseExpDate || null}
                    onChange={(value) => {
                      setFormData({ ...formData, licenseExpDate: value ? new Date(value) : null });
                    }}
                  />
                </ItemWrapper>


                <ItemWrapper>

                  <Box display="flex" justifyContent="space-between">
                    <Box width="40%">
                      <ItemLabel isRequired={hasELDPermission ? true : false}>{messages["settingsDriver_registrationCountry"]}</ItemLabel>
                      <Select
                        id="licenseCountry"
                        placeholder={
                          messages["settingsDriver_registrationCountry"]
                        }
                        required={hasELDPermission ? true : false}
                        valueField="entityId"
                        loading={loadingEntities}
                        requiredMessage={messages["required_field"]}
                        textField="label"
                        options={entityCountryList}
                        value={formData?.licenseCountry || null}
                        onChange={(data) => {
                          if (data?.entityId === "US" || data?.entityId === "CA") {
                            setFormData({
                              ...formData,
                              licenseCountry: data,
                              licenseState: null,
                            });
                          } else {
                            setFormData({
                              ...formData,
                              licenseCountry: data,
                              licenseState: data,
                            });
                          }
                        }}
                        isClearable
                      />
                    </Box>
                    <Box ml={1} width="60%">
                      <ItemLabel isRequired={hasELDPermission && (formData?.licenseCountry?.entityId === "US" || formData?.licenseCountry?.entityId === "CA")}>{messages["settingsDriver_registrationState"]}</ItemLabel>
                      <Select
                        id="licenseState"
                        placeholder={
                          messages["settingsDriver_registrationState"]
                        }
                        required={hasELDPermission && (formData?.licenseCountry?.entityId === "US" || formData?.licenseCountry?.entityId === "CA")}
                        valueField="entityId"
                        loading={loadingEntities}
                        disabled={formData?.licenseCountry?.entityId !== "US" && formData?.licenseCountry?.entityId !== "CA"}
                        requiredMessage={messages["required_field"]}
                        textField="label"
                        options={entityStateList?.filter(item => item.parentId === formData?.licenseCountry?.entityId)}
                        value={
                          formData?.licenseCountry
                            ? formData?.licenseState || null
                            : null
                        }
                        onChange={(data) => {
                          setFormData({ ...formData, licenseState: data });
                        }}
                        isClearable
                      />
                    </Box>
                  </Box>
                </ItemWrapper>
                <ItemWrapper>
                  <Box style={{ marginTop: "-10px" }}>
                    <Select
                      id="unit"
                      label={messages["settingsDriver_vehicle"]}
                      placeholder={messages["settingsDriver_vehicle"]}
                      valueField="id"
                      textField="label"
                      options={vehicles}
                      value={formData?.unit || null}
                      onChange={(data) => {
                        setFormData({ ...formData, unit: data });
                      }}
                      isClearable
                    />
                  </Box>
                </ItemWrapper>
                <Box style={{ marginTop: "10px" }}>
                  <ItemWrapper>
                    <ItemLabel isRequired={true}>{messages["settingsDriver_timezone"]}</ItemLabel>
                    <Select
                      id="timezone"
                      placeholder={messages["settingsDriver_timezone"]}
                      valueField="id"
                      textField="name"
                      options={timeZones}
                      value={formData?.timezone || null}
                      onChange={(data) => {
                        setFormData({ ...formData, timezone: data });
                      }}
                      loading={loadingTimeZones}
                      isClearable
                      required
                      requiredMessage={messages["required_field"]}
                    />
                  </ItemWrapper>
                </Box>

                {useSupervisorPermission(userSecurity, currentSubUser) && (
                  <ItemWrapper>
                    <Select
                      id="supervisor"
                      label={messages["settingsDriver_supervisor"]}
                      placeholder={
                        messages["settingsDriver_supervisorPlaceHolder"]
                      }
                      valueField="id"
                      textField="fullname"
                      options={supervisor}
                      value={formData?.supervisor || null}
                      onChange={(data) => {
                        setFormData({ ...formData, supervisor: data });
                      }}
                      isClearable
                    />
                  </ItemWrapper>
                )}
                <ItemWrapper>
                  <Box style={{ marginTop: "10px", marginBottom: "10px" }}>
                    <TagsManage
                      onlyTags={true}
                      mode="selector"
                      enableSelectAll={true}
                      disableUntaggedSelection={true}
                      placeholder={messages["search"]}
                      requiredAsteriskPosition="start"
                      labelFontSize={12}
                      label={messages["settingsDriver_tags"]}
                      required={true}
                      showError={showTagsError}
                      value={formData?.tags || []}
                      onChange={(items) => {
                        setFormData({ ...formData, tags: items });
                        if(items?.length > 0) {
                          setShowTagsError(false);
                        }
                      }}
                      menuStyle={{ maxHeight: "280px", maxWidth: "400px" }}
                      isVisiblePill={true}
                    />
                    {showTagsError && (
                      <small className="select-error text-danger">
                        {messages["unit_settings_tags_required"]}
                      </small>
                    )}
                  </Box>
                </ItemWrapper>

                {hasKeyFobPermissions && (
                  <ItemWrapper>
                    <ItemLabel isRequired={(hasZonarELDPermissions && !formData?.empNum) ? true : false}>{messages["settingsDriver_keyFobHex"]}</ItemLabel>
                    <Input
                      id="keyFobHex"
                      type={hasZonarELDPermissions ? "number" : "text"}
                      onlyPositive
                      preventOnChange
                      placeholder={messages["settingsDriver_keyFobHex"]}
                      value={formData?.keyFobHex || ""}
                      onChange={(value) => {
                        setFormData({ ...formData, keyFobHex: value });
                      }}
                      required={(hasZonarELDPermissions && !formData?.empNum) ? true : false}
                      requiredMessage={messages["required_field"]}
                      maxLength={20}
                      minLengthMessage={`${messages["settingsDriver_minLengthMessageKeyFobHex"]}`}
                      maxLengthMessage={`${messages["settingsDriver_maxLengthMessageKeyFobHex"]}`}
                    />
                    <CounterString string={formData?.keyFobHex || ""} maxLength={20} />
                  </ItemWrapper>
                )}

                <Box p='10px 16px 0px 16px' pb={0}>
                  <p
                    style={{
                      fontWeight: "bold",
                      margin: 0,
                      marginBottom: "8px",
                    }}
                  >
                    {messages["settingsDriver_contactDetails"]}
                  </p>
                  <Divider />
                </Box>
                <Box style={{ marginTop: "10px" }}>
                  <ItemWrapper>
                    <ItemLabel isRequired={formData?.notifyByEmail ? true : false}>{messages["settingsDriver_email"]}</ItemLabel>
                    <Input
                      id="email"
                      type="text"
                      required={formData?.notifyByEmail ? true : false}
                      requiredMessage={messages["required_field"]}
                      placeholder={messages["settingsDriver_email"]}
                      value={formData?.email || ""}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          email: value,
                          notifyByEmail: value ? formData?.notifyByEmail : false,
                        });
                      }}
                      maxLength={50}
                      maxLengthMessage={`${messages["settingsDriver_maxLengthMessageName"]} 50`}
                    />
                    <CounterString string={formData?.email || ""} maxLength={50} />
                  </ItemWrapper>
                </Box>
                <ItemWrapper>
                  <Switch
                    optionLabels={[
                      messages["settingsDriver_yes"],
                      messages["settingsDriver_no"],
                    ]}
                    isDisabled={!formData?.email}
                    label={messages["settingsDriver_notifyByEmail"]}
                    size="medium"
                    checked={formData?.notifyByEmail}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        notifyByEmail: !formData?.notifyByEmail,
                      })
                    }
                  />
                </ItemWrapper>
                <ItemWrapper>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ width: "45%", marginRight: "5px" }}>
                      <Select
                        id="type"
                        label={messages["settingsDriver_phoneNumber"]}
                        placeholder={messages["settingsDriver_indicator"]}
                        valueField="id"
                        required={formData?.notifyBySms ? true : false}
                        requiredMessage={messages["required_field"]}
                        textField="code"
                        getOptionLabel={(opt) => `${opt?.code} (+${opt?.id})`}
                        options={countries}
                        value={formData?.indicator}
                        onChange={(data) => {
                          setFormData({ ...formData, indicator: data });
                        }}
                        isClearable
                      />
                    </div>
                    <div style={{ width: "55%", marginTop: "15px" }}>
                      <Input
                        id="phone"
                        type="text"
                        placeholder={messages["settingsDriver_phone"]}
                        value={formData?.phone}
                        onChange={(value) => {
                          let valueNumber = value.replace(/\D/g, "");
                          setFormData({
                            ...formData,
                            phone: valueNumber,
                            notifyBySms:
                              valueNumber.length === 10
                                ? formData?.notifyBySms
                                : false,
                          });
                        }}
                        regex={/^\d*$/}
                        maxLength={10}
                        maxLengthMessage={`${messages["accountContacts_maxLengthMessageCellPhone"]} 10`}
                        minLength={10}
                        minLengthMessage={`${messages["accountContacts_minLengthMessageCellPhone"]} 10`}
                        required={formData?.notifyBySms ? true : false}
                        requiredMessage={messages["required_field"]}
                      />
                    </div>
                  </div>
                  <CounterString string={formData?.phone || ""} maxLength={10} />
                </ItemWrapper>
                <Box px="16px">
                  <Switch
                    optionLabels={[
                      messages["settingsDriver_yes"],
                      messages["settingsDriver_no"],
                    ]}
                    isDisabled={!formData?.phone}
                    label={messages["settingsDriver_notifyBySms"]}
                    size="medium"
                    checked={formData?.notifyBySms}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        notifyBySms: !formData?.notifyBySms,
                      })
                    }
                  />
                </Box>

                {Boolean(HasPermission(user, "zlogs")) && (
                  <>
                    <Box p={2} pb={0}>
                      <p
                        style={{
                          fontWeight: "bold",
                          margin: 0,
                          marginTop: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        Zonar ELD
                      </p>
                      <Divider />
                    </Box>

                    <Box style={{ marginTop: "10px" }}>
                      <ItemWrapper>
                        <ItemLabel isRequired={hasELDPermission ? true : false}>{messages["settingsDriver_zonarELD"]}</ItemLabel>
                        <Select
                          id="hosRulesetIds"
                          placeholder={messages["settingsDriver_zonarELD"]}
                          valueField="entityId"
                          loading={loadingEntities}
                          requiredMessage={messages["required_field"]}
                          textField="label"
                          required={hasELDPermission ? true : false}
                          getOptionLabel={(opt) => `${opt?.label} ${opt?.value2}`}
                          options={sortByLabel(entityHosRuleSetList)}
                          value={formData?.hosRulesetIds || null}
                          onChange={(data) => {
                            setFormData({
                              ...formData,
                              hosExempt: data ? formData?.hosExempt : false,
                              hosRulesetIds: data,
                            });
                          }}
                          isClearable
                        />
                      </ItemWrapper>
                    </Box>

                    <Box style={{ marginTop: "10px", paddingLeft: "16px", marginBottom: "10px" }}>
                      <Switch
                        optionLabels={[
                          messages["settingsDriver_yes"],
                          messages["settingsDriver_no"],
                        ]}
                        isDisabled={!formData?.hosRulesetIds}
                        label={messages["settingsDriver_zonarELDExempt"]}
                        size="medium"
                        checked={formData?.hosExempt}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            hosExempt: !formData?.hosExempt,
                          })
                        }
                      />
                    </Box>

                    {formData?.hosExempt && (
                      <>
                        <ItemWrapper>
                          <Box
                            p={0}
                            display="flex"
                            alignItems="baseline"
                            flexDirection="row"
                          >
                            {formData?.hosExempt && (
                              <p
                                style={{
                                  color: "red",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                  marginRight: "4px",
                                  marginBottom: "0px",
                                  marginTop: "0px",
                                }}
                              >
                                *
                              </p>
                            )}
                            <p
                              style={{
                                fontWeight: "bold",
                                margin: 0,
                              }}
                            >
                              {messages["settingsDriver_zonarELDExemptComment"]}
                            </p>
                          </Box>
                          <TextArea
                            style={{ fontFamily: '"Outfit", sans-serif' }}
                            id="hosExemptComment"
                            required={formData?.hosExempt ? true : false}
                            requiredMessage={messages["required_field"]}
                            placeholder={
                              messages["settingsDriver_zonarELDExemptComment"]
                            }
                            maxLength={255}
                            value={formData?.hosExemptComment || ""}
                            onChange={(value) =>
                              setFormData({
                                ...formData,
                                hosExemptComment: value,
                              })
                            }
                          />
                          <CounterString string={formData?.hosExemptComment || ""} maxLength={255} />
                        </ItemWrapper>
                      </>
                    )}

                    <ItemWrapper>
                      <ItemLabel>{messages["settingsDriver_specialDutyStatuses"]}</ItemLabel>
                      <Box width='95%' display='flex' alignItems='center' justifyContent='space-between' mt='15px'>
                        <Box>
                          <Checkbox
                            id="personalConveyance"
                            label={messages["settingsDriver_personalConveyance"]}
                            checked={formData?.personalConveyance}
                            size="small"
                            onChange={(checked) => {
                              if (!checked) {
                                setFormData({ ...formData, allowedPCDistance: 0, personalConveyance: false });
                                return;
                              }
                              setFormData({
                                ...formData,
                                personalConveyance: true,
                                allowedPCDistance: getDefaultPcDistance(isKph),
                              });
                            }}
                          />
                          <Checkbox
                            id="personalYardMoves"
                            label={messages["settingsDriver_personalYardMoves"]}
                            checked={formData?.personalYardMoves}
                            size="small"
                            onChange={(value) => {
                              setFormData({ ...formData, personalYardMoves: value });
                            }}
                          />
                        </Box>
                        <Box display='flex' alignItems='center' width='150px' justifyContent='center' flexDirection='row'>
                          <Box>
                            
                            <ItemLabel isRequired={formData?.personalConveyance}>{messages["settingsDriver_allowedPCDistance"]}</ItemLabel>
                            <Input
                              id="allowedPCDistance"
                              disabled={!formData?.personalConveyance}
                              placeholder="0"
                              style={{ width: "100px" }}
                              value={formData?.allowedPCDistance ?? ""}
                              type="number"
                              required={!!formData?.personalConveyance}
                              onlyPositive
                              max={999}
                              onChange={(value) => {
                                const maxValue = isKph
                                  ? Math.round(convertMilesToKm(999))
                                  : 999;

                                const numVal =
                                  value === "" || value === null
                                    ? ""
                                    : Math.max(
                                        0,
                                        Math.min(maxValue, Number(value))
                                      );
                                // Preservar hasta 5 decimales (p.ej. 8.04672 = 5 millas)
                                const cleaned =
                                  numVal === ""
                                    ? ""
                                    : Number.isFinite(numVal)
                                      ? Math.round(numVal * 100000) / 100000
                                      : numVal;

                                setFormData({
                                  ...formData,
                                  allowedPCDistance: cleaned,
                                });
                              }}
                            />
                          </Box>
                          <p style={{ fontSize: '12px', margin: '5px 0px 0px 10px' }}>{measure}</p>
                        </Box>
                      </Box>
                    </ItemWrapper>

                  </>

                )}

                {!formData?.id && (
                  <Box px="16px">
                    <PasswordDriverFields
                      formData={formData}
                      setFormData={setFormData}
                      isRequiredPwd={isRequiredPwd}
                      errorMatchPassword={errorMatchPassword}
                      setErrorMatchPassword={setErrorMatchPassword}
                    />
                  </Box>
                )}
                {driverById && (
                  <ItemWrapper>
                    <Select
                      id="status"
                      label={messages["settingsDriver_status"]}
                      placeholder={messages["settingsDriver_status"]}
                      valueField="value"
                      textField="name"
                      options={statusOptionDriver}
                      value={formData?.status || statusOptionDriver[0]}
                      onChange={(data) => {
                        setFormData({ ...formData, status: data });
                      }}
                    />
                  </ItemWrapper>
                )}
              </Grid>
            </Form>
          </div>
        </ModalBody>

        <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
          <Button onClick={handleClose} className="btn-cancel">
            {messages["cancel"]}
          </Button>
          <Button
            className="btn-blue"
            onClick={(e) => {
              e.preventDefault();
              if (!formData?.tags || formData.tags.length === 0) {
                setShowTagsError(true);
              }

              formRef.current.submit();
            }}
          >
            {messages["save"]}
          </Button>
        </ModalFooter>
      </Modal>
      <CreateEditTag />
    </>
  );
};

export default CreateEditDriver;
